import { Label } from "../label";
import { useController } from "react-hook-form";
import SearchableSelector from "./SearchableSelector";
import { useEffect, useId, useMemo, useRef } from "react";

export default function SelectController({
  errors,
  control,
  name,
  title,
  options = [],
  className = "",
  defaultValue,
  /** Optional requirement star rendered after the title (any label-safe node). */
  titleSuffix,
}: any) {
  const { field } = useController({
    name,
    control,
    defaultValue,
  });

  const isDisabled = options.length === 0;
  const labelId = useId();

  const optionValues = useMemo(
    () => options.map((o: any) => String(o.value)),
    [JSON.stringify(options.map((o: any) => o.value))]
  );

  const fieldOnChangeRef = useRef(field.onChange);
  fieldOnChangeRef.current = field.onChange;

  useEffect(() => {
    if (!field.value || optionValues.length === 0) return;

    if (!optionValues.includes(String(field.value))) {
      setTimeout(() => fieldOnChangeRef.current(undefined), 0);
    }
  }, [optionValues, field.value]);

  // `SearchableSelector` takes `{ name, value }` with a string value — the
  // callers here pass ids that are sometimes numbers.
  const selectorOptions = useMemo(
    () =>
      options.map((o: any) => ({
        name: String(o.name ?? ""),
        value: String(o.value),
      })),
    [options]
  );

  return (
    <div
      className={`flex flex-col gap-1.5 [&:last-child:nth-child(odd)]:col-span-2 ${className}`}
    >
      <Label
        id={labelId}
        htmlFor={`${name}-${title}`}
        className="text-[#051912] text-sm tracking-[-0.56px] font-normal leading-[1.35]"
      >
        {title}
        {titleSuffix}
      </Label>

      <SearchableSelector
        options={selectorOptions}
        value={field.value != null ? String(field.value) : ""}
        onChange={field.onChange}
        disabled={isDisabled}
        // These lists are curated upstream (roles, users, intakes), so keep
        // the order the caller handed us.
        preserveOrder
        placeholder={isDisabled ? `No ${title} Found` : `Select ${title}`}
        searchInputPlaceholder={`Search ${String(title).toLowerCase()}`}
        aria-labelledby={labelId}
        triggerClassName={`bg-[#fcfcfc] border-[#cdd1d0] h-10 rounded-xl px-3 py-2 text-sm w-full ${
          errors?.[name] ? "border-red-500" : ""
        } ${isDisabled ? "opacity-60 cursor-not-allowed" : ""}`}
      />
      {errors?.[name]?.message ? (
        <p className="text-sm text-red-500 mt-1">
          {String(errors[name].message)}
        </p>
      ) : null}
    </div>
  );
}
